import { create } from 'zustand';
import type { User } from '@supabase/supabase-js';
import { AdminUser, UserRole } from '@/types';
import { supabase } from '@/lib/supabase';

interface AuthState {
    user: AdminUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    // Sign in via the browser client (sets the session cookies AND populates this
    // store in one path). Returns the resolved role so the caller can route. Used
    // by the customer login form.
    signIn: (email: string, password: string) => Promise<{ ok: boolean; role: UserRole | null }>;
    // Staff-only sign in — rejects (and signs out) non-staff accounts. Used by the
    // admin login form. Thin wrapper around signIn.
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    clearError: () => void;
    checkAuth: () => Promise<void>;
    // Sync local state to "signed out" WITHOUT calling supabase.auth.signOut().
    // Used by the onAuthStateChange listener to react to SIGNED_OUT / empty sessions.
    syncSignedOut: () => void;
}

const STAFF_ROLES: UserRole[] = ['super_admin', 'admin', 'manager'];

// Never expose raw Supabase error messages — they reveal internal details (user enumeration, etc.)
const GENERIC_LOGIN_ERROR = 'Invalid email or password. Please try again.';

// While an explicit signIn()/login() call is populating the store, the
// onAuthStateChange SIGNED_IN handler must NOT also schedule a checkAuth() — the
// in-flight call is the single source of truth for that sign-in. Without this guard
// the SIGNED_IN event (which fires DURING signInWithPassword, before the store is
// set) races the explicit call and flips isLoading back to true after it settled,
// re-fetching the profile and leaving the login button stuck spinning.
let sessionEstablishing = false;

// The subset of the `profiles` row this store reads.
interface ProfileRow {
    name?: string | null;
    role?: string | null;
    phone?: string | null;
    date_of_birth?: string | null;
    saved_addresses?: unknown[] | null;
    avatar?: string | null;
    last_login?: string | null;
    created_at?: string | null;
    password_changed?: boolean | null;
}

// Build the app's user shape from a Supabase auth user + its profile row.
function buildUser(authUser: User, profile: ProfileRow | null): AdminUser {
    return {
        id: authUser.id,
        email: authUser.email || '',
        name: profile?.name || '',
        role: (profile?.role as UserRole) || 'customer',
        phone: profile?.phone ?? undefined,
        dateOfBirth: profile?.date_of_birth ?? undefined,
        savedAddresses: profile?.saved_addresses || [],
        avatar: profile?.avatar ?? undefined,
        lastLogin: profile?.last_login ?? undefined,
        createdAt: profile?.created_at || authUser.created_at,
        passwordChanged: profile?.password_changed ?? true,
    };
}

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: true, // Start true while we check initial session
    error: null,

    signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        sessionEstablishing = true;

        try {
            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (authError || !authData.user) {
                // Expected failure (wrong credentials → 400) that's fully handled with a
                // generic UI message — warn, don't error, so the Next.js dev overlay
                // doesn't flag every mistyped password as an application error.
                if (authError) console.warn('[auth] signInWithPassword failed:', authError.status);
                set({ user: null, isAuthenticated: false, isLoading: false, error: GENERIC_LOGIN_ERROR });
                return { ok: false, role: null };
            }

            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', authData.user.id)
                .single();

            if (profileError) {
                console.error('[auth] profile fetch failed:', profileError.code);
            }

            const user = buildUser(authData.user, profileData);

            // Fire-and-forget last_login update — not critical path
            supabase
                .from('profiles')
                .update({ last_login: new Date().toISOString() })
                .eq('id', authData.user.id)
                .then(({ error }) => {
                    if (error) console.error('[auth] last_login update failed:', error.code);
                });

            set({ user, isAuthenticated: true, isLoading: false, error: null });
            return { ok: true, role: user.role };
        } catch {
            // Catch-all — never expose stack traces or internal messages
            set({ user: null, isAuthenticated: false, isLoading: false, error: GENERIC_LOGIN_ERROR });
            return { ok: false, role: null };
        } finally {
            sessionEstablishing = false;
        }
    },

    login: async (email: string, password: string) => {
        const { ok, role } = await get().signIn(email, password);
        if (!ok) return false;

        // Reject non-staff accounts silently (same generic message — no role enumeration)
        if (!role || !STAFF_ROLES.includes(role)) {
            await supabase.auth.signOut();
            set({ user: null, isAuthenticated: false, isLoading: false, error: GENERIC_LOGIN_ERROR });
            return false;
        }

        return true;
    },

    logout: async () => {
        await supabase.auth.signOut();
        set({ user: null, isAuthenticated: false, isLoading: false, error: null });
    },

    clearError: () => {
        set({ error: null });
    },

    syncSignedOut: () => {
        set({ user: null, isAuthenticated: false, isLoading: false, error: null });
    },

    checkAuth: async () => {
        set({ isLoading: true });
        try {
            // getUser() re-validates the JWT with Supabase's auth server. NEVER use
            // getSession() here — it trusts the locally-stored token without validation.
            const { data: { user }, error } = await supabase.auth.getUser();

            if (error || !user) {
                // No valid session (anonymous, expired, or revoked token). getUser
                // already clears stale tokens internally on an auth error, so just
                // settle local state — do NOT call signOut() here (see listener note).
                set({ user: null, isAuthenticated: false, isLoading: false });
                return;
            }

            const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

            set({ user: buildUser(user, profileData), isAuthenticated: true, isLoading: false });
        } catch (error) {
            console.error('[auth] session check failed:', error);
            set({ user: null, isAuthenticated: false, isLoading: false });
        }
    },
}));

// Real-time auth listener — browser only (prevents build workers from hanging during SSR).
//
// CRITICAL: this callback must NEVER call supabase.auth.signOut() (directly or via
// logout()). signOut() always emits a SIGNED_OUT event, so reacting to SIGNED_OUT by
// signing out again creates an infinite signOut → SIGNED_OUT → signOut loop that pegs
// the main thread and freezes the tab for every anonymous visitor on every page.
// React to events by syncing LOCAL state only (syncSignedOut), never by re-signing-out.
//
// Per Supabase guidance, other supabase calls (checkAuth → getUser) are deferred with
// setTimeout(0) so they run AFTER this callback returns — calling them inline can deadlock
// on the auth lock held during the callback.
if (typeof window !== 'undefined') {
    supabase.auth.onAuthStateChange((event, session) => {
        const store = useAuthStore.getState();

        if (event === 'SIGNED_OUT') {
            // Supabase has already cleared the session — only sync local state.
            store.syncSignedOut();
            return;
        }

        if (
            event === 'SIGNED_IN' ||
            event === 'TOKEN_REFRESHED' ||
            event === 'INITIAL_SESSION'
        ) {
            if (session?.user) {
                // An explicit signIn()/login() call is already populating the store for
                // this exact event — don't schedule a redundant, racy checkAuth().
                if (sessionEstablishing) return;

                const userChanged = !store.user || store.user.id !== session.user.id;
                if (userChanged) {
                    // Defer out of the callback to avoid auth-lock deadlock.
                    setTimeout(() => useAuthStore.getState().checkAuth(), 0);
                } else if (store.isLoading) {
                    // Same user (e.g. token refresh) and nothing to reload — just settle.
                    useAuthStore.setState({ isLoading: false });
                }
            } else {
                // No session (anonymous visitor) — stop loading. NEVER call signOut here.
                store.syncSignedOut();
            }
        }
    });
}
